
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getMailboxes, handleConsentCallback } from "@/services/mailbox";
import { Mailbox } from "@/lib/types";
import { toast } from "sonner";

export function useMailboxes() {
  const { tenantId } = useAuth();
  const [mailboxes, setMailboxes] = useState<Mailbox[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isConsentProcessing, setIsConsentProcessing] = useState(false);

  const fetchMailboxes = async () => {
    setIsLoading(true);
    const data = await getMailboxes(tenantId);
    setMailboxes(data);
    setIsLoading(false);
  };

  // Update a specific mailbox's rules count
  const updateMailboxRulesCount = (mailboxId: string, rulesCount?: number) => {
    setMailboxes(prevMailboxes => 
      prevMailboxes.map(mailbox => {
        if (mailbox.id === mailboxId) {
          return {
            ...mailbox,
            rules_count: rulesCount !== undefined ? rulesCount : mailbox.rules_count
          };
        }
        return mailbox;
      })
    );
  };

  // Handle consent callback for mailbox connection
  const processConsentCallback = async (code: string) => {
    setIsConsentProcessing(true);
    
    const email = sessionStorage.getItem("pendingMailboxEmail");
    
    if (email) {
      const success = await handleConsentCallback(code, email, tenantId);
      if (success) {
        await fetchMailboxes();
        sessionStorage.removeItem("pendingMailboxEmail");
      }
    } else {
      toast.error("Could not find the email for the mailbox");
    }
    
    setIsConsentProcessing(false);
    
    // Clear the URL parameters
    return true;
  };

  // Initial fetch of mailboxes
  useEffect(() => {
    fetchMailboxes();
  }, [tenantId]);

  return {
    mailboxes,
    isLoading,
    isConsentProcessing,
    fetchMailboxes,
    updateMailboxRulesCount,
    processConsentCallback
  };
}
